import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Check, Clock, FileText, Pencil, Plus, Trash2, Users, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { SharePanel } from "@/components/share-panel";
import { DisclaimerNote, SiteFooter } from "@/components/site-footer";
import { SiteHeader } from "@/components/site-header";
import { StalenessRow } from "@/components/staleness-notice";
import { StatusPill } from "@/components/status-pill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { deleteJob, listJobs, reannotateJob, renameJob, startAnnotation } from "@/lib/jobs.functions";
import { listPendingShares, respondToShare } from "@/lib/sharing.functions";
import { relativeTime } from "@/lib/format";

/**
 * Incoming shares awaiting accept/decline — only ever populated by the
 * matched-existing-account path in shareJob (a token invite stays anonymous
 * until claimed, so it never appears here). Shown above the uploads list so
 * it can't be missed, since accepting is what actually grants access to the
 * shared report — declining just removes the invite with no downstream
 * cleanup needed.
 */
function PendingShares() {
  const queryClient = useQueryClient();
  const listFn = useServerFn(listPendingShares);
  const respondFn = useServerFn(respondToShare);

  const { data: pending } = useQuery({
    queryKey: ["pending-shares"],
    queryFn: () => listFn({}),
    refetchInterval: 15000,
  });

  const respondMutation = useMutation({
    mutationFn: (vars: { shareId: string; decision: "accept" | "decline" }) =>
      respondFn({ data: vars }),
    onSuccess: (_result, vars) => {
      queryClient.invalidateQueries({ queryKey: ["pending-shares"] });
      if (vars.decision === "accept") queryClient.invalidateQueries({ queryKey: ["jobs"] });
      toast.success(vars.decision === "accept" ? "Report added to your uploads." : "Invite declined.");
    },
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : "Couldn't respond to that invite."),
  });

  if (!pending || pending.length === 0) return null;

  return (
    <div className="mb-8 space-y-3">
      {pending.map((share) => (
        <article
          key={share.shareId}
          className="flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-accent/40 bg-accent/5 p-5"
        >
          <div className="flex items-center gap-2 text-sm">
            <Users className="size-4 shrink-0 text-accent" />
            <span>
              <strong>{share.ownerEmail}</strong> wants to share <strong>{share.filename}</strong>{" "}
              with you.
            </span>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={respondMutation.isPending}
              onClick={() => respondMutation.mutate({ shareId: share.shareId, decision: "decline" })}
            >
              Decline
            </Button>
            <Button
              size="sm"
              disabled={respondMutation.isPending}
              onClick={() => respondMutation.mutate({ shareId: share.shareId, decision: "accept" })}
            >
              Accept
            </Button>
          </div>
        </article>
      ))}
    </div>
  );
}

/**
 * Inline-editable job title: shows `label` when set, else falls back to the
 * uploaded `filename` — the label never changes the actual file, just how
 * this job displays (useful for telling apart e.g. one upload per family
 * member without renaming the underlying VCF).
 */
function JobTitle({
  jobId,
  filename,
  label,
  canRename,
  onRenamed,
}: {
  jobId: string;
  filename: string;
  label: string | null;
  canRename: boolean;
  onRenamed: () => void;
}) {
  const renameFn = useServerFn(renameJob);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(label ?? "");

  if (!canRename) {
    return <h2 className="font-display text-lg break-all">{label || filename}</h2>;
  }

  const renameMutation = useMutation({
    mutationFn: (newLabel: string) => renameFn({ data: { jobId, label: newLabel.trim() || null } }),
    onSuccess: () => {
      setEditing(false);
      onRenamed();
    },
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : "Couldn't rename this upload."),
  });

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <Input
          autoFocus
          value={draft}
          placeholder={filename}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") renameMutation.mutate(draft);
            if (event.key === "Escape") {
              setDraft(label ?? "");
              setEditing(false);
            }
          }}
          className="h-8 max-w-xs"
        />
        <Button
          size="icon"
          variant="ghost"
          className="size-8 shrink-0"
          disabled={renameMutation.isPending}
          onClick={() => renameMutation.mutate(draft)}
        >
          <Check className="size-4" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="size-8 shrink-0"
          onClick={() => {
            setDraft(label ?? "");
            setEditing(false);
          }}
        >
          <X className="size-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="group flex items-center gap-2">
      <h2 className="font-display text-lg break-all">{label || filename}</h2>
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100"
        title="Rename this upload"
      >
        <Pencil className="size-3.5" />
      </button>
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/dashboard")({
  head: () => ({
    meta: [
      { title: "My uploads — Understand Your Genome" },
      {
        name: "description",
        content:
          "Track every DNA file you've uploaded, watch its annotation progress, and explore your annotated file when it's ready.",
      },
      { property: "og:title", content: "My uploads — Understand Your Genome" },
      {
        property: "og:description",
        content: "Watch your annotation progress and open your annotated file.",
      },
    ],
  }),
  component: DashboardPage,
});

function DashboardPage() {
  const fetchJobs = useServerFn(listJobs);
  const startFn = useServerFn(startAnnotation);
  const queryClient = useQueryClient();

  const { data: jobs, isLoading } = useQuery({
    queryKey: ["jobs"],
    queryFn: () => fetchJobs({}),
    refetchInterval: 4000,
  });

  const startMutation = useMutation({
    mutationFn: (vars: { jobId: string }) => startFn({ data: vars }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["jobs"] }),
    onError: (error) =>
      toast.error(
        error instanceof Error ? error.message : "Couldn't start annotation. Please try again.",
      ),
  });

  const reannotateFn = useServerFn(reannotateJob);
  const reannotateMutation = useMutation({
    mutationFn: (vars: { jobId: string }) => reannotateFn({ data: vars }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["jobs"] }),
    onError: (error) =>
      toast.error(
        error instanceof Error ? error.message : "Couldn't reprocess this sample. Please try again.",
      ),
  });

  // Available regardless of status -- deleteJob itself never restricted this
  // server-side, but the only place a delete action existed in the UI was
  // the report page, which is only reachable once a job is 'ready'. A file
  // that's still 'uploaded' (never started), 'queued'/'annotating' (in
  // progress), or 'failed' had no way to be removed at all before this.
  const deleteFn = useServerFn(deleteJob);
  const deleteMutation = useMutation({
    mutationFn: (vars: { jobId: string }) => deleteFn({ data: vars }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["jobs"] }),
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : "Couldn't delete that. Please try again."),
  });

  function handleDelete(jobId: string) {
    if (!window.confirm("Delete this upload? This can't be undone.")) return;
    deleteMutation.mutate({ jobId });
  }

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-4xl flex-1 px-5 py-14">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl">Your uploads</h1>
            <p className="mt-2 max-w-xl leading-relaxed text-muted-foreground">
              Each card below is one file you've sent us. Annotation — the work of matching your
              variants against everything scientists already know — happens in the background.
            </p>
          </div>
          <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90">
            <Link to="/upload">
              <Plus className="size-4" /> New upload
            </Link>
          </Button>
        </div>

        <div className="mt-10">
          <PendingShares />
        </div>

        <div className="space-y-4">
          {isLoading && <p className="text-sm text-muted-foreground">Loading your uploads…</p>}

          {!isLoading && (!jobs || jobs.length === 0) && (
            <div className="rounded-3xl border border-dashed border-border bg-card p-10 text-center">
              <FileText className="mx-auto size-8 text-muted-foreground" />
              <h2 className="mt-4 text-xl">Nothing here yet</h2>
              <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
                Upload your first .vcf or .vcf.gz file and we'll start turning it into something
                readable.
              </p>
              <Button asChild className="mt-6 bg-accent text-accent-foreground hover:bg-accent/90">
                <Link to="/upload">Upload a file</Link>
              </Button>
            </div>
          )}

          {jobs?.map((job) => (
            <article
              key={job.id}
              className="rounded-3xl border border-border bg-card p-6 shadow-sm"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <JobTitle
                    jobId={job.id}
                    filename={job.filename}
                    label={job.label}
                    canRename={job.isOwner}
                    onRenamed={() => queryClient.invalidateQueries({ queryKey: ["jobs"] })}
                  />
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                    {job.isOwner ? (
                      <>
                        <Clock className="size-3.5" /> Uploaded {relativeTime(job.created_at)}
                      </>
                    ) : (
                      <>
                        <Users className="size-3.5" /> Shared by {job.sharedByEmail ?? "someone"}
                      </>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill status={job.status} />
                  {job.isOwner && (
                    <button
                      type="button"
                      onClick={() => handleDelete(job.id)}
                      disabled={deleteMutation.isPending}
                      aria-label="Delete this upload"
                      className="inline-flex items-center justify-center rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  )}
                </div>
              </div>

              {job.status === "uploaded" && job.isOwner && (
                <div className="mt-5">
                  <Button
                    onClick={() => startMutation.mutate({ jobId: job.id })}
                    disabled={startMutation.isPending}
                  >
                    Start annotation
                  </Button>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Your file is uploaded. Start annotation when you're ready — it can take a few
                    minutes to a few hours depending on file size.
                  </p>
                </div>
              )}

              {(job.status === "queued" || job.status === "annotating") && (
                <div className="mt-5">
                  <Progress value={job.status === "queued" ? 4 : job.progress} />
                  <p className="mt-2 text-sm text-muted-foreground">
                    Annotation can take anywhere from a few minutes to a few hours depending on how
                    big your file is — we'll email you the moment it's ready. You can close this
                    page.
                  </p>
                </div>
              )}

              {job.status === "failed" && (
                <>
                  <p className="mt-4 rounded-2xl bg-destructive/10 p-3 text-sm text-foreground">
                    Something went wrong with this file
                    {job.error_message ? `: ${job.error_message}` : ""}. Try uploading it again, or
                    get in touch and we'll take a look.
                  </p>
                  {job.isOwner && job.staleness?.hasBenefit && (
                    <StalenessRow
                      staleness={job.staleness}
                      pending={reannotateMutation.isPending}
                      onReprocess={() => reannotateMutation.mutate({ jobId: job.id })}
                    />
                  )}
                </>
              )}

              {job.status === "ready" && (
                <>
                  {job.isOwner && job.staleness?.hasBenefit && (
                    <StalenessRow
                      staleness={job.staleness}
                      pending={reannotateMutation.isPending}
                      onReprocess={() => reannotateMutation.mutate({ jobId: job.id })}
                    />
                  )}
                  <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
                    {job.isOwner ? <SharePanel jobId={job.id} /> : <span />}
                    <Button asChild>
                      <Link to="/report/$jobId" params={{ jobId: job.id }}>
                        Explore annotated file
                      </Link>
                    </Button>
                  </div>
                </>
              )}
            </article>
          ))}
        </div>

        <DisclaimerNote className="mt-10" />
      </main>
      <SiteFooter />
    </div>
  );
}
